import styled from 'styled-components';
import { typography } from 'src/common/styles/typography';
import { CleanButton } from 'src/common/components/Button';
import { SimpleLink } from 'src/common/components/SimpleLink';
import { toast } from 'react-toastify';
import { toastOptions } from 'src/common/components/Toast/config';
import { useHistory } from 'react-router-dom';
import { MainPageRoute } from 'src/routes';
import { FC, ReactNode, useEffect } from 'react';
import { handleRedirect } from 'src/common/services/process';
import { useWebSdk } from 'src/webSdk/useWebSdk';
import { ErrorCode, errorMsgs } from 'src/common/services/errors';
import { getErrorResponse } from 'src/config/axios';
import { IntlFormattedMessage } from '../IntlFormattedMessage';

export type ToastKind = 'primary' | 'error';

export const ToastWrapper = styled.div<{ kind: ToastKind }>`
  width: 100%;
  padding: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;

  background: ${({ theme, kind }) =>
    kind === 'error' ? theme.colors.dangerSoft : theme.text.primary};
  border-radius: 0.5rem;
  color: ${({ theme, kind }) =>
    kind === 'error' ? theme.colors.dangerBorder : theme.colors.white};
  font-family: ${typography.fontFamilyDefault};
  font-size: 1.125rem;
  font-weight: bold;
  line-height: 1.5;

  p {
    max-width: 25.0625rem;
    font-weight: 400;
  }
`;

const Button = styled(CleanButton)`
  min-width: 10.25rem;
  display: flex;
  font-size: 1.125rem;
  font-weight: bold;
  padding: 0.8125rem 0.625rem;
  line-height: 1.9375rem;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: ${({ theme }) => theme.text.primary};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.colors.white};
`;

const StyledLink = styled(SimpleLink)`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  text-decoration: none;
`;

export const ToastBankUnavailable = () => (
  <ToastWrapper kind="primary">
    <p>
      Przepraszamy, obecnie ten bank jest niedostępy, spróbuj wybrać inny bank.{' '}
      <StyledLink>Przeczytaj Więcej</StyledLink>
    </p>
    <Button>Wybierz inny</Button>
  </ToastWrapper>
);

export const ToastLostConnection = () => {
  const history = useHistory();
  return (
    <ToastWrapper kind="error">
      <p>
        Przepraszamy, połączenie zostało zerwane, spróbuj ponownie wybrać bank.
      </p>
      <Button onClick={() => history.push(MainPageRoute)}>
        Rozpocznij od nowa
      </Button>
    </ToastWrapper>
  );
};

interface ErrorToastBaseProps {
  message: string;
  children?: ReactNode;
}

interface RedirectErrorToastBaseProps {
  children?: ReactNode;
}

interface ErrorToastProps extends ErrorToastBaseProps {
  err?: unknown;
  buttonText?: string;
  redirectUrl?: string;
}

const ErrorToastBase: FC<ErrorToastBaseProps> = ({ message, children }) => {
  return (
    <ToastWrapper kind="error">
      <p>{message}</p>
      {children}
    </ToastWrapper>
  );
};

const RedirectErrorToastBase: FC<RedirectErrorToastBaseProps> = ({
  children,
}) => {
  return (
    <ToastWrapper kind="error">
      <IntlFormattedMessage id="error.toast.message" />
      {children}
    </ToastWrapper>
  );
};

const ErrorToast: FC<ErrorToastProps> = ({
  message,
  err,
  buttonText,
  redirectUrl,
}) => {
  const history = useHistory();
  const webSdk = useWebSdk();

  useEffect(() => {
    if (webSdk) {
      webSdk.eventHandlers?.onError?.(err);
    }
  }, []);

  return webSdk ? (
    <ErrorToastBase message={message} />
  ) : (
    <ErrorToastBase message={message}>
      <Button
        onClick={() =>
          redirectUrl
            ? handleRedirect(redirectUrl)
            : history.push(MainPageRoute)
        }
      >
        {buttonText}
      </Button>
    </ErrorToastBase>
  );
};

const RedirectErrorToast: FC<ErrorToastProps> = ({ err }) => {
  const webSdk = useWebSdk();

  useEffect(() => {
    if (webSdk) {
      webSdk.eventHandlers?.onError?.(err);
    }
  }, []);

  return <RedirectErrorToastBase />;
};

const getErrorMsg = (err: unknown) => {
  const res = getErrorResponse(err);
  return errorMsgs[res.errors?.[0].errorCode] || errorMsgs[ErrorCode.Unknown];
};

export const notifyError = (
  err: unknown,
  buttonText: string,
  redirectUrl?: string
) => {
  const message = getErrorMsg(err);

  return toast(
    <ErrorToast
      err={err}
      message={message}
      buttonText={buttonText}
      redirectUrl={redirectUrl}
    />,
    { ...toastOptions, toastId: message }
  );
};

export const notifyRedirectError = (err: unknown) => {
  return toast(<RedirectErrorToast err={err} message={''} />, {
    ...toastOptions,
    toastId: 'redirectMessage',
  });
};
